// All site content, fetched once and shared.
//
// Why one fetch at the root rather than per page: the whole dataset for this
// site is small, and a single request means the four components that used to
// import a static array get their data synchronously from context — no loading
// state of their own, no round trip per route change.
//
// Cached in sessionStorage so only the first page load of a session ever waits.
// The cache is shown immediately and refreshed in the background, so a stale tab
// never blocks on the network.

import { createContext, useContext, useEffect, useState } from "react";
import { fetchContent } from "./sanity/client";
import { CONTENT_QUERY } from "./sanity/queries";

// Bump this whenever the query's shape changes. A cached payload from an older
// shape renders first and would show, for a moment, a page missing whatever the
// new fields feed — empty filter chips, for instance.
const CACHE_KEY = "sanity-content-v3";

const ContentContext = createContext({ data: null, status: "loading", error: null });

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private browsing, blocked storage, corrupt JSON — all non-fatal.
    return null;
  }
};

const writeCache = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Quota or blocked storage. The site works without the cache.
  }
};

export function ContentProvider({ children }) {
  const [state, setState] = useState(() => {
    const cached = readCache();
    return cached
      ? { data: cached, status: "ready", error: null }
      : { data: null, status: "loading", error: null };
  });

  useEffect(() => {
    let cancelled = false;

    fetchContent(CONTENT_QUERY)
      .then((data) => {
        if (cancelled) return;
        setState({ data, status: "ready", error: null });
        writeCache(data);
      })
      .catch((error) => {
        if (cancelled) return;
        // Keep showing the cached copy if we have one — a failed refresh should
        // never blank a page that is already rendering.
        setState((prev) =>
          prev.data ? prev : { data: null, status: "error", error }
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return <ContentContext.Provider value={state}>{children}</ContentContext.Provider>;
}

const useContent = () => useContext(ContentContext);

export const useContentStatus = () => {
  const { status, error } = useContent();
  return { status, error };
};

// Every accessor falls back to an empty value, so a component never has to guard
// against content that has not arrived or a document that was never created.
export const useSettings = () => useContent().data?.settings ?? {};
export const useHome = () => useContent().data?.home ?? {};
export const useWorkPage = () => useContent().data?.workPage ?? {};
export const useNotFoundPage = () => useContent().data?.notFoundPage ?? {};
export const useContact = () => useContent().data?.contact ?? {};

export const useProjectList = () => useContent().data?.projects ?? [];
export const useFeaturedSlugs = () => useContent().data?.featuredSlugs ?? [];
export const useFilterGroups = () => useContent().data?.filterGroups ?? [];
export const useClientList = () => useContent().data?.clients ?? [];
export const useClientOrder = () => useContent().data?.clientOrder ?? [];
